// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import { useMemo, type ReactNode } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";

/* ============================================================================
   Elad Engineering Board — research → redesign
   The diagram-with-live-values pattern (studied from RealiteQ, a Zeus
   competitor) applied to our Engineering Dashboard. Owned by Kai.
   Refactored to the LOCKED board-container standard (Erez 2026-07-19): every
   section is ONE <SectionBox> (neutral-gray, self-hugging, one big black label
   inside), holding WHITE cards; ALL SectionBoxes live in ONE <SectionGrid> in
   ONE React Flow node, rendered through <BoardCanvas>. Nothing outside a box.
   Four sections: Brief · The Pattern (RealiteQ) · Today · The Redesign.
   ========================================================================== */

const FONT =
  "'Inter','SF Pro Display',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

const T = {
  ink: "#0f172a",
  ink2: "#475569",
  mut: "#94a3b8",
  line: "#e6eaf0",
  accent: "#3b6cf6",
  petrol: "#2563eb",
  green: "#16a34a",
  amber: "#d99a1c",
  card: "#ffffff",
  bg: "#E5E5E5",
  dots: "#CFCFCF",
};

const CARD_SHELL: React.CSSProperties = {
  background: T.card,
  borderRadius: 16,
  boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
  fontFamily: FONT,
};

// cache-bust so a re-captured image shows on a board refresh with no redeploy
const bust = "?v=2026-07-20";
const IMG = (name: string) => `/board/elad-engineering/${name}.png${bust}`;

/* --------------------------- white-card components ----------------------- */

function CardNode({ data }: { data: any }) {
  const hi = !!data.highlight;
  return (
    <div
      style={{
        ...CARD_SHELL,
        width: data.width || 460,
        padding: 26,
        border: hi ? `2px solid ${T.petrol}` : `1px solid ${T.line}`,
      }}
    >
      {data.label && (
        <span
          style={{
            display: "inline-block",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: hi ? T.petrol : T.mut,
            border: `1px solid ${hi ? "#c7d6fb" : T.line}`,
            borderRadius: 999,
            padding: "4px 11px",
            marginBottom: 14,
          }}
        >
          {data.label}
        </span>
      )}
      <div
        style={{
          fontSize: 24,
          fontWeight: 800,
          letterSpacing: "-0.01em",
          color: T.ink,
          lineHeight: 1.15,
        }}
      >
        {data.title}
      </div>
      {data.lede && (
        <div
          style={{
            marginTop: 11,
            fontSize: 15.5,
            color: T.ink2,
            lineHeight: 1.55,
            fontWeight: 450,
          }}
        >
          {data.lede}
        </div>
      )}
      {data.bullets && (
        <ul style={{ margin: "14px 0 0", padding: 0, listStyle: "none" }}>
          {data.bullets.map((b: any, i: number) => (
            <li
              key={i}
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 10,
                fontSize: 14.5,
                color: T.ink2,
                lineHeight: 1.5,
              }}
            >
              <span
                style={{
                  flex: "0 0 auto",
                  marginTop: 8,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: b.tone === "amber" ? T.amber : b.tone === "green" ? T.green : T.accent,
                }}
              />
              <span>
                {b.k && (
                  <b style={{ color: T.ink, fontWeight: 700 }}>{b.k} </b>
                )}
                {b.t || b}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FrameNode({ data }: { data: { img: string; label: string; w: number; tone?: string } }) {
  const barTone = data.tone === "us" ? "#eef3ff" : "#f4f6f9";
  const dot = data.tone === "us" ? T.accent : "#cbd2dd";
  return (
    <div style={{ fontFamily: FONT, width: data.w }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "9px 14px",
          background: barTone,
          border: `1px solid ${T.line}`,
          borderBottom: "none",
          borderRadius: "13px 13px 0 0",
        }}
      >
        <span style={{ display: "flex", gap: 6 }}>
          <i style={{ width: 9, height: 9, borderRadius: "50%", background: dot, display: "inline-block" }} />
          <i style={{ width: 9, height: 9, borderRadius: "50%", background: "#dbe1ea", display: "inline-block" }} />
          <i style={{ width: 9, height: 9, borderRadius: "50%", background: "#dbe1ea", display: "inline-block" }} />
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink2, letterSpacing: "0.01em" }}>
          {data.label}
        </span>
      </div>
      <div
        style={{
          border: `1px solid ${T.line}`,
          borderRadius: "0 0 13px 13px",
          overflow: "hidden",
          boxShadow: "0 10px 34px rgba(15,23,42,0.10)",
          background: "#fff",
          fontSize: 0,
          lineHeight: 0,
        }}
      >
        <img src={data.img} alt={data.label} draggable={false} style={{ width: data.w, height: "auto", display: "block" }} />
      </div>
    </div>
  );
}

// a vertical stack of white cards (e.g. a frame's explanation column)
function Col({ children, gap = 24 }: { children: ReactNode; gap?: number }) {
  return <div style={{ display: "flex", flexDirection: "column", gap }}>{children}</div>;
}

// the flex row that lays a section's white cards side by side (wraps if huge)
function Row({ children, gap = 72 }: { children: ReactNode; gap?: number }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap }}>
      {children}
    </div>
  );
}

/* ------------------------------ the sections ----------------------------- */
// Each entry becomes ONE SectionBox: a short black-label title + its white
// cards. Content is rendered as WHITE CardNode / FrameNode components.

function BriefSection() {
  return (
    <SectionBox title="Brief" layout="custom">
      <Row>
        <CardNode
          data={{
            width: 440,
            label: "Elad Engineering · Process View",
            title: "Show the plant, not just the numbers",
            lede:
              "Research → redesign · bring the diagram-with-live-values pattern to our Engineering Dashboard.",
          }}
        />
        <CardNode
          data={{
            width: 900,
            label: "The brief",
            title: "The one pattern we're chasing",
            lede:
              "RealiteQ (a competitor of Zeus) shows the operator a picture of the plant — a process schematic — with the live numbers printed directly on the equipment: flow on the pipe, level in the tank, pressure at the sensor. Our current dashboard is data-rich but abstract (cards + charts). This board studies exactly how RealiteQ does it, then proposes the same idea in our modern design language.",
            bullets: [
              { k: "Studied:", t: "the RealiteQ UI v7.0 manual (48 pp) + 5 live deployments on realiteq.com." },
              { k: "Goal:", t: "a process view where every Zeus reading sits on the schematic, colour-coded by role." },
            ],
          }}
        />
        <CardNode
          data={{
            width: 490,
            label: "Research sources",
            title: "Read in full — not skimmed",
            bullets: [
              { k: "PDF:", t: "RealiteQ UI User Manual v7.0 — all 48 pages, incl. §5 Process screen + §16 Draw editor." },
              { k: "Live:", t: "SAWACO gauges, American Water NJ map, Carmiel reservoir, Maagan-Michael well." },
              { k: "Verified:", t: "our own live dashboard at my-jarvis-dashboard-engineering.pages.dev." },
            ],
          }}
        />
      </Row>
    </SectionBox>
  );
}

function PatternSection() {
  return (
    <SectionBox title="The Pattern" layout="custom">
      <Row>
        <CardNode
          data={{
            width: 440,
            label: "01 · The pattern to beat",
            title: "How RealiteQ does it",
            lede: "Values overlaid on a process schematic — the flagship SCADA pattern.",
          }}
        />
        <FrameNode
          data={{
            img: IMG("realiteq-process"),
            label: "RealiteQ UI · §5 Process screen — live values on the plant",
            w: 860,
          }}
        />
        <Col>
          <CardNode
            data={{
              width: 470,
              label: "The mechanism",
              title: "Numbers printed on the equipment",
              lede:
                "A drawing of the pumping station with live values sitting directly on it: 85% on the pump, 10.4 at the sensor, 2.2 m as the tank's water fill. On/Off buttons, 'Over load' / 'No feedback' status lamps, a green 'High level' check. The value is read twice — as a number AND as a graphic (the tank fills, the pump turns green when running).",
            }}
          />
          <CardNode
            data={{
              width: 470,
              label: "Live-site finding",
              title: "Chips are colour-coded by ROLE",
              bullets: [
                { k: "Pink", t: "= a live measured value (level, volume).", tone: "amber" },
                { k: "Orange", t: "= a setpoint / threshold.", tone: "amber" },
                { k: "Blue", t: "= a drive/instrument reading (RPM, Hz)." },
                { k: "Green", t: "= an active state or timer.", tone: "green" },
              ],
            }}
          />
        </Col>
        <FrameNode
          data={{
            img: IMG("realiteq-editor"),
            label: "RealiteQ UI · §16 Draw editor — how the screens are built",
            w: 680,
          }}
        />
        <Col>
          <CardNode
            data={{
              width: 470,
              label: "How it's authored",
              title: "Background image + linked value chips",
              lede:
                "Two stages: (1) import a picture of the plant as the background; (2) drop 'link' elements on top, each bound to a node by its path, positioned by x/y coordinates, with a format string (%2.1f bar → '15.1 bar'). Colours, visibility and position all driven by the live value. That's the whole trick — and it's exactly how we'll build ours.",
            }}
          />
          <CardNode
            data={{
              width: 470,
              label: "Their weakness = our wedge",
              title: "Three screens, but a dated look",
              lede:
                "RealiteQ ships three coordinated views — process mimic, gauge dashboard, geographic map. But the craft is mid-2010s: skeuomorphic 3D buttons, glossy gauges, pastel, light-only, dense. Same information, 2011 design. A modern, flat, theme-aware schematic with the same density is a clear wedge.",
            }}
          />
        </Col>
      </Row>
    </SectionBox>
  );
}

function TodaySection() {
  return (
    <SectionBox title="Today" layout="custom">
      <Row>
        <CardNode
          data={{
            width: 440,
            label: "02 · Where we are today",
            title: "Our dashboard today",
            lede:
              "Data-rich cards + charts — live on my-jarvis-dashboard-engineering.pages.dev.",
          }}
        />
        <FrameNode
          data={{
            img: IMG("ours-current"),
            label: "my-jarvis-dashboard-engineering.pages.dev — Shomrat Gadol (live)",
            w: 1020,
            tone: "us",
          }}
        />
        <Col>
          <CardNode
            data={{
              width: 470,
              highlight: true,
              label: "The gap",
              title: "Accurate — but abstract",
              lede:
                "Everything is real Zeus data (Flow 26.89 m³/h, Pressure 3.16 bar, Uptime 99.7%) and the PI agent briefs on load. But it's KPI cards + charts: the operator reads numbers without ever seeing the plant. There's no picture that says 'this is your pump, this is your tank, here's what each one is doing right now.'",
            }}
          />
          <CardNode
            data={{
              width: 470,
              label: "Keep",
              title: "What already works",
              bullets: [
                { k: "", t: "Live per-client Zeus reads, scoped by WorkOS org.", tone: "green" },
                { k: "", t: "The in-browser PI agent (auto-briefs on load).", tone: "green" },
                { k: "", t: "Honest N/A — never a fabricated number.", tone: "green" },
              ],
            }}
          />
        </Col>
      </Row>
    </SectionBox>
  );
}

function RedesignSection() {
  return (
    <SectionBox title="The Redesign" layout="custom">
      <Row>
        <CardNode
          data={{
            width: 440,
            label: "03 · The redesign · proposal",
            title: "A process view — our way",
            lede: "The RealiteQ idea, executed in our 2026 design language. Mockup →",
          }}
        />
        <FrameNode
          data={{
            img: IMG("ours-redesign"),
            label: "PROPOSED · Engineering Dashboard — Process View (mockup)",
            w: 1280,
            tone: "us",
          }}
        />
        <Col>
          <CardNode
            data={{
              width: 470,
              highlight: true,
              label: "Design decision · the overlay",
              title: "Live Zeus values, on the plant",
              lede:
                "A clean schematic of Shomrat Gadol's 3-stage ozone treatment: intake → pump P-101 → three O₃ contact tanks → treated-water outlet. Every reading sits where it belongs — FM-102 flow on the pipe, PT-101 pressure at the sensor, DO₂ per stage on each tank, level as the tank's fill.",
            }}
          />
          <CardNode
            data={{
              width: 470,
              label: "Design decision · colour = role",
              title: "Our own role-coding",
              bullets: [
                { k: "Measured", t: "— clean white chip, blue label." },
                { k: "Setpoint", t: "— dashed amber outline (DO₂ target ≥ 5.0).", tone: "amber" },
                { k: "State/OK", t: "— green pill (pump RUNNING, PLC connected).", tone: "green" },
                { k: "Offline", t: "— honest N/A: PT-203 shown as 'field check', never faked.", tone: "amber" },
              ],
            }}
          />
          <CardNode
            data={{
              width: 470,
              label: "Design decision · the craft",
              title: "2026, not 2011",
              lede:
                "Light, flat, elegant — Inter, hairline strokes, soft water fills, a pump that turns green when running and flow arrows on the pipes. Same information density as RealiteQ, none of the skeuomorphic weight. Keeps our KPI strip on top and an embedded mini-trend in the corner (their idea), theme-aware.",
            }}
          />
        </Col>
      </Row>
    </SectionBox>
  );
}

/* ------------------------------ the ONE node ----------------------------- */
// The whole board is ONE node: a SectionGrid packing all SectionBoxes into a
// compact wide rectangle.
function GridNode() {
  return (
    <SectionGrid>
      <BriefSection />
      <PatternSection />
      <TodaySection />
      <RedesignSection />
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  grid: GridNode as unknown as NodeTypes[string],
};

/* -------------------------------- page ----------------------------------- */

export function EladEngineeringBoardPage() {
  const nodes = useMemo<Node[]>(
    () => [{ id: "board-grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: {} }],
    [],
  );

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.12} />
    </div>
  );
}

export default EladEngineeringBoardPage;
